import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Radio from '../../../atoms/Radio/Radio';
import Table from '../../../atoms/Table/Table';
import CheckboxOptionLabel from '../../CheckboxOptionLabel/CheckboxOptionLabel';
import { isOptionSelected } from '../../../../domain/radioList/radioListUseCase';
import { parseFontAwesomeClass } from '../../../../utils/fontawesome/config';
import cn from '../../../../utils/cn';

import styles from './RadioListField.module.css';

/**
 * Radio list form field with grouped options and composite cell labels.
 * Exclusive selection (one option). When layout is `column` and field-level
 * `headers` are present, options render as a table.
 * @param {object} props
 * @param {CheckboxListHeader[]} [props.headers]
 * @param {CheckboxListGroup[]} [props.groups]
 * @param {string} [props.value]
 * @param {(id: string) => void} props.onChange
 * @param {string} [props.id]
 * @param {string} [props.name]
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.isReadOnly=false]
 * @param {string} [props.error]
 * @param {string} [props.ariaDescribedBy]
 * @returns {JSX.Element}
 */
function RadioListField({
  headers = [],
  groups = [],
  value = '',
  onChange,
  id: fieldIdAttr,
  name,
  disabled = false,
  isReadOnly = false,
  error = '',
  ariaDescribedBy,
}) {
  const groupList = Array.isArray(groups) ? groups : [];
  const selectedValue = value == null ? '' : String(value);
  const headerList = Array.isArray(headers) ? headers : [];
  const groupName = name || fieldIdAttr;

  const handleSelect = (optionId) => {
    if (isReadOnly) return;
    onChange(String(optionId));
  };

  if (groupList.length === 0) {
    return <div className={styles.empty}>-</div>;
  }

  /**
   * @param {CheckboxListOption[]} options
   * @returns {JSX.Element}
   */
  const renderTableOptions = (options) => (
    <div className={styles['table-wrapper']}>
      <Table width="100%">
        <>
          <thead>
            <tr>
              <th
                className={cn([styles['table-header-cell'], styles['table-radio-cell']])}
                aria-label="Sélection"
              />
              {headerList.map((header, headerIndex) => (
                <th
                  key={`header-${header.lib}-${header.width}`}
                  className={cn([
                    styles['table-header-cell'],
                    headerIndex > 0 && styles['table-divider'],
                  ])}
                  style={{ width: header.width || 'auto' }}
                >
                  {header.lib}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {options.map((option) => {
              const optionId = String(option.id);
              const radioId = `${fieldIdAttr}-option-${optionId}`;
              const checked = isOptionSelected(selectedValue, optionId);
              const cells = Array.isArray(option.cells) ? option.cells : [];

              return (
                <tr key={optionId}>
                  <td className={cn([styles['table-body-cell'], styles['table-radio-cell']])}>
                    <Radio
                      id={radioId}
                      name={groupName}
                      value={optionId}
                      checked={checked}
                      onChange={() => handleSelect(optionId)}
                      disabled={disabled}
                      isReadOnly={isReadOnly}
                      size="small"
                      theme="black"
                    />
                  </td>
                  {headerList.map((header, cellIndex) => {
                    const cell = cells[cellIndex];
                    return (
                      <td
                        key={`${optionId}-col-${header.lib}-${header.width}`}
                        className={cn([
                          styles['table-body-cell'],
                          cellIndex > 0 && styles['table-divider'],
                        ])}
                        style={{ width: header.width || 'auto' }}
                      >
                        {cell ? (
                          <CheckboxOptionLabel cells={[cell]} />
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </>
      </Table>
    </div>
  );

  /**
   * @param {'wrap' | 'column'} layout
   * @param {CheckboxListOption[]} options
   * @returns {JSX.Element}
   */
  const renderListOptions = (layout, options) => (
    <div
      className={cn([
        styles.options,
        layout === 'column' ? styles['options-column'] : styles['options-wrap'],
      ])}
    >
      {options.map((option) => {
        const optionId = String(option.id);
        const radioId = `${fieldIdAttr}-option-${optionId}`;
        const checked = isOptionSelected(selectedValue, optionId);

        return (
          <div key={optionId} className={styles.option}>
            <Radio
              id={radioId}
              name={groupName}
              value={optionId}
              checked={checked}
              onChange={() => handleSelect(optionId)}
              disabled={disabled}
              isReadOnly={isReadOnly}
              size="small"
              theme="black"
              multiline
              label={(
                <CheckboxOptionLabel cells={option.cells} />
              )}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className={cn([styles.container, error && styles['has-error']])}
      aria-describedby={ariaDescribedBy}
    >
      {groupList.map((group) => {
        const layout = group.layout === 'column' ? 'column' : 'wrap';
        const options = Array.isArray(group.options) ? group.options : [];
        const groupKey = group.lib
          || options.map((option) => option.id).join('-')
          || 'group-empty';
        const groupIcon = group.icon ? parseFontAwesomeClass(group.icon) : null;
        const groupIconColor = group.iconColor || undefined;
        const groupBackgroundColor = group.backgroundColor || undefined;
        const hasGroupHeader = Boolean(group.lib || group.icon);
        const useTableLayout = layout === 'column' && headerList.length > 0;

        return (
          <fieldset
            key={groupKey}
            className={styles.group}
          >
            {hasGroupHeader ? (
              <legend
                className={styles['group-legend']}
                style={{
                  color: groupIconColor,
                  backgroundColor: groupBackgroundColor,
                }}
              >
                {groupIcon ? (
                  <FontAwesomeIcon
                    icon={/** @type {any} */ (groupIcon)}
                    className={styles['group-icon']}
                  />
                ) : null}
                {group.lib ? <span>{group.lib}</span> : null}
              </legend>
            ) : null}

            {useTableLayout
              ? renderTableOptions(options)
              : renderListOptions(layout, options)}
          </fieldset>
        );
      })}
    </div>
  );
}

export default RadioListField;
